import {
  accessAsset,
  anyAsset,
  AssetUIModel,
  CanPipe,
  FormatFileSizePipe,
  FormatFileTypesPipe,
  SpDateFormatPipe,
  SpTimeFormatPipe,
} from '@thehubz/client/core';
import { Asset, CreateAssetDto } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { FormFields } from '../../components/asset-detail-view/asset-info/models/asset-info.interface';

const AccessAsset = accessAsset;
const AnyAsset = anyAsset;
const AssetTypes = CreateAssetDto.AssetTypeEnum;
export interface AssetMetadata {
  customerName: CustomClients;
  metadata: FormFields[];
}

export const lufthansaMetadataConfig = (assetInfo: AssetUIModel, canPipe: CanPipe, brandingKey?: string): AssetMetadata => {
  const status: { id: Asset.StatusEnum; name: string }[] = [
    {
      id: Asset.StatusEnum.Uploaded,
      name: $localize`Uploaded`,
    },
  ];
  /**
   * Below condition is used to check whether a user have right to set asset status 'Approved'.
   */
  if (canPipe.transform(AnyAsset, 'update.allstatus')) {
    status.push({
      id: Asset.StatusEnum.Approved,
      name: $localize`Approved`,
    });
  }
  /**
   * Below condition is used to check whether a user have right to set asset status 'Archived'.
   */
  if (canPipe.transform(AnyAsset, 'archived')) {
    status.push({
      id: Asset.StatusEnum.Archived,
      name: $localize`Archived`,
    });
  }
  return {
    customerName: brandingKey === CustomClients.LufthansaMediacenter ? CustomClients.LufthansaMediacenter : CustomClients.LufthansaMedialounge,
    metadata: [
      {
        fieldLabel: $localize`Name`,
        editable: true,
        formControlName: 'name',
      },
      {
        fieldLabel: $localize`Created at`,
        editable: false,
        formControlName: 'createdAt',
        pipe: new SpDateFormatPipe(),
      },
      {
        fieldLabel: $localize`IPTC creation date`,
        editable: false,
        directValue: true,
        formControlName: 'dateCreated',
        pipe: new SpDateFormatPipe(),
        customClients: CustomClients.LufthansaMedialounge,
      },
      {
        fieldLabel: $localize`Status`,
        editable: assetInfo && assetInfo.accessKeys ? canPipe.transform(AccessAsset(assetInfo), 'update.allstatus') : true,
        formControlName: 'status',
        options: status,
      },
      {
        fieldLabel: $localize`File size`,
        editable: false,
        formControlName: 'size',
        propertyObject: 'mainFile',
        pipe: new FormatFileSizePipe(),
      },
      {
        fieldLabel: $localize`File type`,
        editable: false,
        formControlName: 'ext',
        propertyObject: 'mainFile',
        pipe: new FormatFileTypesPipe(),
      },
      {
        fieldLabel: $localize`Image width pixels`,
        editable: false,
        formControlName: 'widthString',
        propertyObject: 'metaData',
        assetType: AssetTypes.Image,
      },
      {
        fieldLabel: $localize`Image height pixels`,
        editable: false,
        formControlName: 'heightString',
        propertyObject: 'metaData',
        assetType: AssetTypes.Image,
      },
      {
        fieldLabel: $localize`User group`,
        editable: true,
        formControlName: 'brand',
      },
      {
        fieldLabel: $localize`Photographer`,
        editable: false,
        customValue: true,
        formControlName: 'iptc_photoGrapher',
        propertyObject: 'metaData',
        assetType: AssetTypes.Image,
      },
      {
        fieldLabel: $localize`Source`,
        editable: false,
        customValue: true,
        formControlName: 'iptc_source',
        propertyObject: 'metaData',
        assetType: AssetTypes.Image,
      },
      {
        fieldLabel: $localize`Comment`,
        editable: true,
        customValue: true,
        formControlName: 'adminComment',
        propertyObject: 'metaData',
      },
      {
        fieldLabel: $localize`MBps`,
        editable: false,
        formControlName: 'mbps',
        propertyObject: 'metaData',
        assetType: AssetTypes.Video,
      },
      {
        fieldLabel: $localize`Fps`,
        editable: false,
        formControlName: 'fps',
        propertyObject: 'metaData',
        assetType: AssetTypes.Video,
      },
      {
        fieldLabel: $localize`Audio format`,
        editable: false,
        formControlName: 'audioFormat',
        propertyObject: 'metaData',
        assetType: AssetTypes.Video,
      },
      {
        fieldLabel: $localize`Duration`,
        editable: false,
        formControlName: 'durationString',
        directValue: true,
        pipe: new SpTimeFormatPipe(),
        assetType: AssetTypes.Video,
      },
      {
        fieldLabel: $localize`Codec`,
        editable: false,
        formControlName: 'codec_name',
        propertyObject: 'metaData',
        assetType: AssetTypes.Video,
      },
      {
        fieldLabel: $localize`Original file size`,
        editable: false,
        formControlName: 'originalFileSize',
        propertyObject: 'metaData',
        assetType: AssetTypes.Video,
      },
      {
        fieldLabel: $localize`Release date from`,
        editable: assetInfo && assetInfo.accessKeys ? canPipe.transform(AccessAsset(assetInfo), 'update.allstatus') : true,
        formControlName: 'publishStartDate',
        pipe: new SpDateFormatPipe(),
      },
      {
        fieldLabel: $localize`Release date to`,
        editable: assetInfo && assetInfo.accessKeys ? canPipe.transform(AccessAsset(assetInfo), 'update.allstatus') : true,
        formControlName: 'publishEndDate',
        pipe: new SpDateFormatPipe(),
      },
      {
        fieldLabel: $localize`Creator`,
        editable: false,
        customValue: true,
        formControlName: 'iptc_creator',
        propertyObject: 'metaData',
        assetType: AssetTypes.Video,
      },
      {
        fieldLabel: $localize`Copyright flag`,
        editable: false,
        customValue: true,
        formControlName: 'iptc_copyRight',
        propertyObject: 'metaData',
        assetType: AssetTypes.Video,
      },
      {
        fieldLabel: $localize`Description`,
        editable: false,
        customValue: true,
        formControlName: 'iptc_description',
        propertyObject: 'metaData',
      },
    ],
  };
};
